/* ==========================================================================
   WAYPOINT — Login screen (UI-SPEC screen 1). Night sky, drifting stars, RV
   silhouette on a road line, two profile cards, custom haptic PIN pad.
   ========================================================================== */

import { useEffect, useMemo, useState } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AuthUserSummary } from '../lib/types';
import { loginAction } from '../lib/store';
import { tick, error as errorHaptic, success as successHaptic } from '../lib/haptics';
import { navigate } from '../lib/router';
import { BackspaceIcon, ChevronIcon } from '../shell/icons';

const PIN_LENGTH = 6;
const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'];

interface StarFieldProps {
  count?: number;
}

function StarField({ count = 60 }: StarFieldProps) {
  const stars = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        top: Math.random() * 62,
        left: Math.random() * 100,
        size: Math.random() * 1.6 + 0.6,
        delay: Math.random() * 8,
        duration: 6 + Math.random() * 10,
      })),
    [count],
  );

  return (
    <div className="starfield" aria-hidden="true">
      {stars.map((s) => (
        <span
          key={s.id}
          className="star"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function RvSilhouette() {
  return (
    <svg
      className="rv-silhouette"
      viewBox="0 0 220 60"
      aria-hidden="true"
      fill="none"
    >
      <path
        d="M8 44h204"
        stroke="rgba(246,241,231,0.22)"
        strokeWidth="2"
        strokeDasharray="4 8"
        strokeLinecap="round"
      />
      {/* Tow rig: pickup + the 18ft Wildwood FSX travel trailer — the whole
          point of this app is what's hitched behind the truck. */}
      <path
        d="M24 44V32a4 4 0 0 1 4-4h12l12-9h20a5 5 0 0 1 5 5v20"
        fill="#0E1714"
        stroke="var(--gold)"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <rect x="42" y="25" width="9" height="8" rx="1.2" fill="var(--gold)" opacity="0.85" />
      <path d="M77 37h18" stroke="var(--gold)" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="95" y="16" width="100" height="25" rx="6" fill="#0E1714" stroke="var(--gold)" strokeWidth="1.4" />
      <rect x="106" y="23" width="12" height="8" rx="1.4" fill="var(--gold)" opacity="0.6" />
      <rect x="124" y="23" width="12" height="8" rx="1.4" fill="var(--gold)" opacity="0.85" />
      <rect x="168" y="23" width="13" height="18" rx="1.6" fill="none" stroke="var(--gold)" strokeWidth="1.2" opacity="0.7" />
      <circle cx="38" cy="44" r="5.5" fill="#0E1714" stroke="var(--gold)" strokeWidth="1.4" />
      <circle cx="64" cy="44" r="5.5" fill="#0E1714" stroke="var(--gold)" strokeWidth="1.4" />
      <circle cx="145" cy="44" r="6" fill="#0E1714" stroke="var(--gold)" strokeWidth="1.4" />
    </svg>
  );
}

export function Login() {
  const [users, setUsers] = useState<AuthUserSummary[] | null>(null);
  const [selected, setSelected] = useState<AuthUserSummary | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pinError, setPinError] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const shakeControls = useAnimationControls();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    api
      .getUsers()
      .then(setUsers)
      .catch(() => setLoadError('Could not reach Waypoint. Check the server is running.'));
  }, []);

  useEffect(() => {
    if (pin.length === PIN_LENGTH && selected) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  async function submit() {
    if (!selected) return;
    setBusy(true);
    try {
      await loginAction(selected.id, pin);
      successHaptic();
      navigate('/', { replace: true });
    } catch (err) {
      errorHaptic();
      setPinError(true);
      // Surface the server's actual failure — a lockout looks identical to a
      // typo if every error collapses into "didn't match" (a real agent chased
      // its own tail over exactly this). rate_limited carries a useful message.
      if (err instanceof ApiError && err.code === 'rate_limited') {
        setPinMessage(err.message || 'Too many attempts — wait a couple of minutes, then try again.');
      } else if (err instanceof ApiError && err.code === 'bad_pin') {
        setPinMessage("That PIN didn't match — try again.");
      } else if (err instanceof ApiError) {
        setPinMessage(err.message || 'Login failed — try again.');
      } else {
        setPinMessage("Couldn't reach Waypoint — check your connection and try again.");
      }
      if (reduceMotion) {
        // Reduced-motion users get a color pulse on the dots (CSS) instead
        // of the framer shake — tokens.css collapses transitions but not
        // this JS-driven animation, so it must be gated explicitly here.
      } else {
        shakeControls.start({
          x: [0, -10, 10, -8, 8, -4, 4, 0],
          transition: { duration: 0.42 },
        });
      }
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function pressKey(key: string) {
    if (busy) return;
    if (key === 'back') {
      tick();
      setPinError(false);
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (key === '' || pin.length >= PIN_LENGTH) return;
    tick();
    setPinError(false);
    setPin((p) => p + key);
  }

  return (
    <div className="login surface-night">
      <StarField />

      <div className="login__sky-content">
        <h1 className="login__title text-display">Waypoint</h1>
        <p className="login__subtitle text-caption">Jeff &amp; Elisa · on the road</p>
        <RvSilhouette />
      </div>

      <div className="login__panel">
        {loadError && <p className="login__error">{loadError}</p>}

        {!selected && users && (
          <div className="login__profiles">
            {users.map((u) => (
              <motion.button
                key={u.id}
                type="button"
                className="profile-card"
                style={{ ['--ring-color' as string]: `var(--${u.avatarColor}, var(--ember))` }}
                whileTap={{ scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 550, damping: 32 }}
                onClick={() => {
                  tick();
                  setSelected(u);
                }}
              >
                <span className="profile-card__ring">
                  <span className="profile-card__initial">{u.name.charAt(0)}</span>
                </span>
                <span className="profile-card__name text-title">{u.name}</span>
              </motion.button>
            ))}
          </div>
        )}

        {!selected && !users && !loadError && (
          <div className="login__profiles" aria-hidden="true">
            <div className="profile-card profile-card--skeleton" />
            <div className="profile-card profile-card--skeleton" />
          </div>
        )}

        {selected && (
          <motion.div className="login__pinpad" animate={shakeControls}>
            <button
              type="button"
              className="login__back"
              onClick={() => { setSelected(null); setPin(''); setPinError(false); }}
            >
              <ChevronIcon size={16} className="login__back-chevron" />
              Not {selected.name}?
            </button>
            <p className="login__pin-name text-title">{selected.name}</p>
            <div className={`login__dots${pinError ? ' login__dots--error' : ''}`} aria-hidden="true">
              {Array.from({ length: PIN_LENGTH }, (_, i) => (
                <span key={i} className={`login__dot${i < pin.length ? ' login__dot--filled' : ''}`} />
              ))}
            </div>
            <p className="login__pin-feedback" aria-live="assertive">
              {pinError ? pinMessage : ''}
            </p>
            <div className="login__pad">
              {PAD_KEYS.map((key, i) =>
                key === '' ? (
                  <span key={`pad-spacer-${i}`} />
                ) : (
                  <motion.button
                    key={key}
                    type="button"
                    className="login__key"
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 550, damping: 32 }}
                    onClick={() => pressKey(key)}
                    aria-label={key === 'back' ? 'Delete digit' : `Digit ${key}`}
                  >
                    {key === 'back' ? <BackspaceIcon size={20} /> : key}
                  </motion.button>
                ),
              )}
            </div>
          </motion.div>
        )}
      </div>

      <style>{`
        .login {
          position: relative;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding-top: var(--sat);
        }
        .starfield {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, #060a09 0%, #0E1714 55%, #16241E 100%);
        }
        .star {
          position: absolute;
          border-radius: 50%;
          background: #F6F1E7;
          opacity: 0.7;
          animation: star-drift ease-in-out infinite alternate;
        }
        @keyframes star-drift {
          from { transform: translate(0, 0); opacity: 0.35; }
          to   { transform: translate(6px, -4px); opacity: 0.9; }
        }
        .login__sky-content {
          position: relative;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          align-items: center;
          padding: 24px 24px 8px;
          text-align: center;
          color: var(--on-night);
          min-height: 220px;
        }
        .login__title { color: var(--on-night); margin-bottom: 4px; }
        .login__subtitle { color: color-mix(in srgb, var(--on-night) 65%, transparent); margin-bottom: 20px; }
        .rv-silhouette { width: 100%; max-width: 320px; height: auto; }
        .login__panel {
          position: relative;
          background: var(--canvas);
          border-radius: 26px 26px 0 0;
          padding: 28px 20px calc(28px + var(--sab));
          min-height: 46dvh;
        }
        .login__error { color: var(--danger); text-align: center; }
        .login__profiles {
          display: flex;
          gap: 14px;
          justify-content: center;
        }
        .profile-card {
          flex: 1;
          max-width: 160px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 20px 12px;
          background: var(--canvas-raised);
          border-radius: var(--radius-card);
          box-shadow: var(--shadow-card);
        }
        .profile-card--skeleton {
          height: 132px;
          background: linear-gradient(90deg, var(--canvas-raised), var(--cloud), var(--canvas-raised));
          background-size: 200% 100%;
          animation: shimmer 1.4s ease-in-out infinite;
        }
        @keyframes shimmer { to { background-position: -200% 0; } }
        .profile-card__ring {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          border: 3px solid var(--ring-color, var(--ember));
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--canvas);
        }
        .profile-card__initial {
          font-family: var(--font-display);
          font-size: 26px;
          color: var(--ink);
        }
        .profile-card__name { color: var(--ink); }
        .login__pinpad { display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .login__back {
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 44px;
          padding: 0 4px;
          margin-left: -4px;
          color: var(--ink-soft);
          font-size: 14px;
        }
        .login__back-chevron { transform: scaleX(-1); }
        .login__pin-name { color: var(--ink); }
        .login__dots { display: flex; gap: 14px; }
        .login__dot {
          width: 14px; height: 14px; border-radius: 50%;
          border: 1.5px solid var(--ink-soft);
          transition: background-color 0.15s, border-color 0.15s;
        }
        .login__dot--filled { background: var(--ember); border-color: var(--ember); }
        .login__dots--error .login__dot { border-color: var(--danger); }
        .login__dots--error .login__dot--filled { background: var(--danger); border-color: var(--danger); }
        .login__pin-feedback {
          min-height: 18px;
          font-size: 13px;
          font-weight: 600;
          color: var(--danger);
          text-align: center;
        }
        .login__pad {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          width: 100%;
          max-width: 300px;
        }
        .login__key {
          min-height: 60px;
          border-radius: var(--radius-card);
          background: var(--canvas-raised);
          box-shadow: var(--shadow-card);
          font-family: var(--font-display);
          font-size: 22px;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default Login;
